import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Collapse,
  Divider,
} from "@mui/material";
import {
  LocationOn,
  AddCircleOutline,
  ExpandMore,
  ExpandLess,
  CheckCircle,
} from "@mui/icons-material";
import SelectState from "../../../containers/SelectOptions/StateSelect";
import SelectMunicipality from "../../../containers/SelectOptions/MunicipalitySelect";
import AuthContext from "../../../Context/auth/AuthContext";
import DirectionSelect from "../../../containers/SelectOptions/DirectionSelect";
import DirectionsContext from "../../../Context/Directions/DirectionsContext";

// ── Paleta MerryColor ───────────────────────────────────────
const MC = {
  primary: "#801F56",
  primaryLight: "#A93B7A",
  accent: "#E46EA7",
  accentSoft: "#F3CDD4",
  blushLight: "#FFF0F5",
  white: "#FFFFFF",
  dark: "#2D1B30",
  grey: "#6B6B6B",
  success: "#4CAF50",
};

export const CheckoutStep1 = ({ onNext, setData, direction, setDirection }) => {
  const { autenticado } = useContext(AuthContext);
  const { directions, GetDirections, storeDirection } =
    useContext(DirectionsContext);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [state, setState] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);

  const detectarCambiosDirection = (e) => {
    setDirection(e.target.value);
  };

  const detectarCambiosState = (e) => {
    setState(e.target.value);
  };

  const detectarCambiosMunicipality = (e) => {
    setMunicipality(e.target.value);
  };

  const reset = () => {
    setValue("name", "", { shouldDirty: true });
    setValue("street", "", { shouldDirty: true });
    setValue("between_street", "", { shouldDirty: true });
    setValue("and_between_street", "", { shouldDirty: true });
    setValue("number_int", "", { shouldDirty: true });
    setValue("number_ext", "", { shouldDirty: true });
    setState(null);
    setMunicipality(null);
    setValue("city", "", { shouldDirty: true });
    setValue("zone", "", { shouldDirty: true });
    setValue("postal_code", "", { shouldDirty: true });
    setValue("phone", "", { shouldDirty: true });
    setValue("email", "", { shouldDirty: true });
    setValue("references", "", { shouldDirty: true });
  };

  const onSubmit = (data) => {
    data.country = "Mexico";
    data.state_id = state;
    data.municipality_id = municipality;
    storeDirection(data);
    reset();
    setShowNewForm(false);
  };

  useEffect(() => {
    GetDirections();
  }, []);

  // ── Estilos comunes para inputs ─────────────────────────
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&.Mui-focused fieldset": { borderColor: MC.primary },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: MC.primary },
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* ─── Dirección existente ────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `2px solid ${direction ? MC.accent : MC.accentSoft}`,
          background: direction
            ? `linear-gradient(145deg, ${MC.blushLight} 0%, ${MC.white} 100%)`
            : MC.white,
          transition: "all 0.3s ease",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <LocationOn sx={{ color: MC.primary, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700} color={MC.dark}>
            Dirección de envío
          </Typography>
          {direction && (
            <CheckCircle sx={{ color: MC.success, fontSize: 20, ml: "auto" }} />
          )}
        </Box>

        {directions && directions.length > 0 ? (
          <>
            <Typography
              variant="body2"
              sx={{ color: MC.grey, mb: 1.5 }}
            >
              Selecciona dónde quieres recibir tu pedido:
            </Typography>
            <DirectionSelect
              setDirection={setDirection}
              direction={direction}
              detectarCambiosDirection={detectarCambiosDirection}
            />
          </>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: MC.grey, fontStyle: "italic" }}
          >
            No tienes direcciones guardadas. Agrega una nueva abajo.
          </Typography>
        )}

        <Divider sx={{ my: 2.5, borderColor: MC.accentSoft }} />

        {/* ─── Botón para mostrar/ocultar formulario nuevo ───── */}
        <Button
          fullWidth
          variant={showNewForm ? "contained" : "outlined"}
          onClick={() => setShowNewForm(!showNewForm)}
          startIcon={showNewForm ? <ExpandLess /> : <AddCircleOutline />}
          endIcon={!showNewForm ? <ExpandMore /> : null}
          sx={{
            borderColor: MC.primary,
            color: showNewForm ? MC.white : MC.primary,
            backgroundColor: showNewForm ? MC.primary : "transparent",
            "&:hover": {
              borderColor: MC.primaryLight,
              backgroundColor: showNewForm ? MC.primaryLight : MC.blushLight,
            },
            borderRadius: 2,
            py: 1.2,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.95rem",
          }}
        >
          {showNewForm ? "Ocultar formulario" : "Agregar nueva dirección"}
        </Button>

        {/* ─── Formulario colapsable ───────────────────────── */}
        <Collapse in={showNewForm} timeout={400}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{ mt: 3 }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={MC.dark}
              sx={{ mb: 2 }}
            >
              📍 Nueva dirección
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nombre de dirección"
                  placeholder="Ej: Mi casa, Oficina"
                  {...register("name", { required: "Campo obligatorio" })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="País"
                  value="México"
                  disabled
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Calle"
                  {...register("street", { required: "Campo obligatorio" })}
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Entre calle"
                  {...register("between_street", {
                    required: "Campo obligatorio",
                  })}
                  error={!!errors.between_street}
                  helperText={errors.between_street?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Y entre calle"
                  {...register("and_between_street", {
                    required: "Campo obligatorio",
                  })}
                  error={!!errors.and_between_street}
                  helperText={errors.and_between_street?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Núm. Int."
                  {...register("number_int")}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Núm. Ext."
                  {...register("number_ext", {
                    required: "Campo obligatorio",
                  })}
                  error={!!errors.number_ext}
                  helperText={errors.number_ext?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SelectState detectarCambiosState={detectarCambiosState} />
              </Grid>
              {state && (
                <Grid item xs={12} sm={6} md={4}>
                  <SelectMunicipality
                    detectarCambiosMunicipality={detectarCambiosMunicipality}
                    state_id={state}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ciudad"
                  {...register("city", { required: "Campo obligatorio" })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Zona / Barrio"
                  {...register("zone", { required: "Campo obligatorio" })}
                  error={!!errors.zone}
                  helperText={errors.zone?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Código Postal"
                  {...register("postal_code", {
                    required: "Campo obligatorio",
                  })}
                  error={!!errors.postal_code}
                  helperText={errors.postal_code?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Teléfono"
                  placeholder="10 dígitos — para paquetería"
                  {...register("phone", {
                    required: "Campo obligatorio",
                    pattern: {
                      value: /^[0-9]{10}$/,
                      message: "Debe tener exactamente 10 dígitos",
                    },
                  })}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Correo Electrónico"
                  placeholder="Para rastreo de paquetería"
                  {...register("email", { required: "Campo obligatorio" })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Referencias"
                  placeholder="Ej: Casa color blanco, portón negro"
                  {...register("references", {
                    required: "Campo obligatorio",
                  })}
                  error={!!errors.references}
                  helperText={errors.references?.message}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<AddCircleOutline />}
                sx={{
                  background: `linear-gradient(135deg, ${MC.primary} 0%, ${MC.accent} 100%)`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${MC.primaryLight} 0%, ${MC.primary} 100%)`,
                  },
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(128, 31, 86, 0.3)",
                }}
              >
                Guardar dirección
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};
